import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Flame, Sun, Compass, Lock, ArrowRight, Check } from "lucide-react";

const ICONS = { Sparkles, Flame, Sun, Compass };

export default function Courses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const active = user && (user.subscription?.status === "active" || user.role === "admin");
  const completed = new Set(user?.progress?.completed_lessons || []);

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen pt-32 pb-24 bg-sanctuary-deep">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-16">
          <div className="eyebrow mb-4">The Curriculum · 道</div>
          <h1 className="font-display text-5xl text-parchment font-light">The Path of Reiki</h1>
          <p className="mt-5 text-text-muted leading-relaxed">
            Four degrees, walked in sequence. {active ? "The whole sanctuary is open to you — begin wherever you are called." : "Preview the first teaching freely, then become a member to receive all four degrees."}
          </p>
        </div>

        <div className="space-y-8">
          {courses.map((c, i) => {
            const Icon = ICONS[c.icon] || Sparkles;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
                <Link to={`/courses/${c.id}`} className="group grid md:grid-cols-[280px_1fr] gap-0 rounded-3xl overflow-hidden bg-sanctuary-card gold-border hover:border-gold/40 transition-colors duration-500" data-testid={`course-card-${i + 1}`}>
                  <div className="relative h-56 md:h-auto overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-[opacity,transform] duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-sanctuary-card/90" />
                    <div className="absolute top-5 left-5 font-jp text-4xl text-gold">{c.japanese_name}</div>
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-9 h-9 rounded-full bg-gold/10 grid place-items-center text-gold"><Icon size={18} /></span>
                      <span className="eyebrow">{c.level_label}</span>
                      {!active && i > 0 && <Lock size={14} className="text-text-dim ml-auto" />}
                    </div>
                    <h2 className="font-display text-3xl text-parchment mb-3">{c.title}</h2>
                    <p className="text-text-muted text-sm leading-relaxed mb-5 max-w-2xl">{c.description}</p>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-gold flex items-center gap-2">{c.lesson_count} teachings</span>
                      {c.has_quiz && <span className="text-sage-light flex items-center gap-2"><Check size={14} /> attunement ceremony</span>}
                      <span className="ml-auto text-gold flex items-center gap-1 group-hover:gap-2 transition-all">Enter <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
